import { OverallStarRating } from '../components/shared/recommendations/types';

type ProfileTier =
  | 'platinum'
  | 'gold'
  | 'silver'
  | 'branded'
  | 'basic'
  | string;
type DepthTier = 'platinum' | 'gold' | 'silver' | 'none' | string;

/**
 * Determines if we should fetch profile rating data for a search result card.
 *
 * Strategy: Fetch for ALL profiles (no tier-based optimization)
 * - Ensures 100% consistency with profile page across all tiers
 * - Handles search API staleness in both directions:
 *   - Search has reviews but profile filters them out
 *   - Search has no reviews but profile has them (stale search data)
 * - Fetching is parallel - minimal wall-clock impact
 */
export const shouldFetchProfileRating = (
  _profileTier?: string | null,
): boolean => true;

/**
 * Checks if a profile tier qualifies for star rating display in search results.
 * Matches the logic used on profile pages (isStarRatingDesignApplicable).
 *
 * Star ratings are shown for:
 * - Platinum profiles
 * - Gold profiles (profileTier: "branded" + depthTier: "gold")
 * - Silver profiles (profileTier: "branded" + depthTier: "silver")
 *
 * NOT shown for:
 * - Basic profiles
 * - Branded profiles without gold/silver depth (e.g., depthTier: "none")
 *
 * Note: Search API returns profileTier: "branded" for both gold and silver tiers.
 * We need depthTier from the profile query to differentiate them.
 */
const isStarRatingEligible = (
  profileTier?: ProfileTier | null,
  depthTier?: DepthTier | null,
): boolean => {
  if (!profileTier) {
    return false;
  }

  // Platinum always qualifies
  if (profileTier === 'platinum') {
    return true;
  }

  // Branded only qualifies if it's actually gold or silver depth
  if (profileTier === 'branded') {
    return depthTier === 'gold' || depthTier === 'silver';
  }

  // Basic and others do not qualify
  return false;
};

/**
 * Determines the final rating to display in search results.
 * Always uses profile data for 100% consistency across all tiers.
 *
 * Requirements:
 * - Must have reviews within last 12 months (numberOfReviewsRecent > 0)
 * - Must be eligible based on profile tier (platinum, gold, or silver)
 * - Ensures consistency with profile page star rating display
 */
export const getSearchResultRating = ({
  searchRecentReviewCount,
  profileRating,
  profileReviewCount,
  profileLoading,
  profileTier,
  depthTier,
}: {
  searchRecentReviewCount?: number | null;
  profileRating?: number | null;
  profileReviewCount?: number | null;
  profileLoading: boolean;
  profileTier?: string | null;
  depthTier?: string | null;
}): OverallStarRating | undefined => {
  // Check tier eligibility - must be platinum, gold, or silver
  if (!isStarRatingEligible(profileTier, depthTier)) {
    return undefined;
  }

  if (searchRecentReviewCount === 0) {
    return undefined;
  }

  if (profileLoading) {
    return undefined;
  }

  const profileHasRating =
    profileRating !== null &&
    profileRating !== undefined &&
    profileReviewCount !== null &&
    profileReviewCount !== undefined &&
    profileReviewCount > 0;

  if (!profileHasRating) {
    return undefined;
  }

  const displayReviewCount =
    searchRecentReviewCount !== null && searchRecentReviewCount !== undefined
      ? Math.min(profileReviewCount, searchRecentReviewCount)
      : profileReviewCount;

  if (displayReviewCount === 0) {
    return undefined;
  }

  return {
    rating: profileRating,
    totalReviews: displayReviewCount,
  };
};
